import {
	type ImportDefinitionContract,
	type ImportDefinitionListDTO,
	type ImportDefinitionVersionDTO,
	TenantConfigPlane,
} from '@today/types'
import { apiClient } from './api.client'

/** The import-definition route tree of a console plane (TO-414 REQ-012/013; TO-632 de-prefixed). */
const definitionsOf = (tenantId: string, plane: TenantConfigPlane) =>
	plane === TenantConfigPlane.PLATFORM
		? `/admin/tenants/${encodeURIComponent(tenantId)}/import-definitions`
		: '/import-definitions'

const versionOf = (tenantId: string, plane: TenantConfigPlane, key: string, version: number) =>
	`${definitionsOf(tenantId, plane)}/${encodeURIComponent(key)}/versions/${version}`

/** A tenant's versioned import definitions (TO-414), as the designer saves them (TO-400 BR-12). */
export const importDefinitionsService = {
	async list(tenantId: string, plane: TenantConfigPlane): Promise<ImportDefinitionListDTO> {
		const { data } = await apiClient.get<ImportDefinitionListDTO>(definitionsOf(tenantId, plane))
		return data
	},

	async getVersion(tenantId: string, plane: TenantConfigPlane, key: string, version: number) {
		const { data } = await apiClient.get<ImportDefinitionVersionDTO>(versionOf(tenantId, plane, key, version))
		return data
	},

	/** `POST …/import-definitions` — version 1 as a DRAFT. */
	async create(tenantId: string, plane: TenantConfigPlane, key: string, contract: ImportDefinitionContract) {
		const { data } = await apiClient.post<ImportDefinitionVersionDTO>(definitionsOf(tenantId, plane), { key, contract })
		return data
	},

	/** `POST …/{key}/versions` — the next version as a DRAFT. */
	async addVersion(tenantId: string, plane: TenantConfigPlane, key: string, contract: ImportDefinitionContract) {
		const { data } = await apiClient.post<ImportDefinitionVersionDTO>(
			`${definitionsOf(tenantId, plane)}/${encodeURIComponent(key)}/versions`,
			{ contract },
		)
		return data
	},

	/** `PUT …/{key}/versions/{version}` — replaces a DRAFT, guarded by the revision last read. */
	async updateDraft(
		tenantId: string,
		plane: TenantConfigPlane,
		key: string,
		version: number,
		expectedRevision: number,
		contract: ImportDefinitionContract,
	) {
		const { data } = await apiClient.put<ImportDefinitionVersionDTO>(versionOf(tenantId, plane, key, version), {
			expectedRevision,
			contract,
		})
		return data
	},

	/** `POST …/{key}/versions/{version}/activate`. */
	async activate(tenantId: string, plane: TenantConfigPlane, key: string, version: number) {
		const { data } = await apiClient.post<ImportDefinitionVersionDTO>(
			`${versionOf(tenantId, plane, key, version)}/activate`,
		)
		return data
	},
}
